<script lang="ts">
  import { untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { classifyPhoneNumber } from "../../../../../../../../../../../../../../../../../api/kehrblatt/customer-registry/contact-points"
  import type {
    DemoResetProfileDraft,
    DemoResetProfileOutput,
  } from "../../../../_logic/demo-data-reset-contract"
  import { buildDemoResetProfileOutput, validateDemoResetProfile } from "./_logic/profileValidation"

  const {
    initial,
    otherAccessEmail = null,
    submitLabel = "Weiter",
    onOutput,
  }: {
    initial: DemoResetProfileDraft
    otherAccessEmail?: string | null
    submitLabel?: string
    onOutput: (output: DemoResetProfileOutput) => void
  } = $props()

  const startingValues = untrack(() => ({ ...initial }))
  let accessEmail = $state(startingValues.accessEmail)
  let password = $state(startingValues.password)
  let contactEmail = $state(startingValues.contactEmail)
  let contactMobile = $state(startingValues.contactMobile)
  let validatingMobile = $state(false)
  let mobileValidationError = $state<string | null>(null)

  const draft = $derived<DemoResetProfileDraft>({
    accessEmail,
    password,
    contactEmail,
    contactMobile,
    passwordReusable: initial.passwordReusable,
    reuseUserId: initial.reuseUserId,
  })
  const validation = $derived(validateDemoResetProfile(draft, otherAccessEmail))
  const passwordOptional = $derived(initial.passwordReusable && Boolean(initial.reuseUserId))
  const passwordPlaceholder = $derived(
    passwordOptional
      ? "Passwort (leer lassen, um das bestehende zu behalten)"
      : "Passwort (erforderlich – mindestens 12 Zeichen)",
  )
  const passwordHelper = $derived(
    validation.errors.password ??
      (passwordOptional
        ? "Leer lassen, um das bestehende Passwort unverändert zu übernehmen."
        : "Vergeben Sie ein neues Passwort mit 12 bis 128 Zeichen."),
  )
  const canSubmit = $derived(validation.valid && !validatingMobile)

  const save = async () => {
    if (!canSubmit) return

    validatingMobile = true
    mobileValidationError = null
    try {
      const classified = await classifyPhoneNumber(contactMobile.trim())

      if (
        classified.suggested_kind !== "MOBILE" ||
        classified.confidence !== "HIGH" ||
        !classified.normalized_value
      ) {
        mobileValidationError = "Bitte geben Sie eine eindeutig erkennbare Mobilnummer ein."

        return
      }

      contactMobile = classified.normalized_value
      onOutput(buildDemoResetProfileOutput(draft, classified.normalized_value))
    } catch {
      mobileValidationError =
        "Die Mobilnummer konnte nicht geprüft werden. Bitte versuchen Sie es erneut."
    } finally {
      validatingMobile = false
    }
  }
</script>

<div class="profile-editor form light">
  <div class="profile-editor__fields">
    <Input
      id="demo-reset-access-email"
      type="email"
      placeholder="Zugangsmail (erforderlich)"
      autocomplete="email"
      bind:value={accessEmail}
      helperText={validation.errors.accessEmail ??
        "Mit dieser E-Mail erfolgt später die Anmeldung."}
      required={true}
      disabled={validatingMobile}
    />
    <Input
      id="demo-reset-password"
      type="password-native"
      placeholder={passwordPlaceholder}
      autocomplete="new-password"
      bind:value={password}
      helperText={passwordHelper}
      required={!passwordOptional}
      disabled={validatingMobile}
    />
    <Input
      id="demo-reset-contact-email"
      type="email"
      placeholder="Kontaktmail – Ansprechpartner (erforderlich)"
      autocomplete="email"
      bind:value={contactEmail}
      helperText={validation.errors.contactEmail ??
        "Diese Adresse erhalten die Beispiel-Ansprechpartner."}
      required={true}
      disabled={validatingMobile}
    />
    <Input
      id="demo-reset-contact-mobile"
      type="tel"
      placeholder="Kontaktmobil – Ansprechpartner (erforderlich)"
      autocomplete="tel"
      bind:value={contactMobile}
      helperText={mobileValidationError ??
        validation.errors.contactMobile ??
        "Diese Mobilnummer erhalten die Beispiel-Ansprechpartner."}
      required={true}
      disabled={validatingMobile}
    />
  </div>
  <div class="profile-editor__actions">
    <CTA
      type="primaryButton"
      content={validatingMobile ? "Mobilnummer wird geprüft…" : submitLabel}
      onClick={save}
      disabled={!canSubmit}
    />
  </div>
</div>

<style lang="less">
  .profile-editor,
  .profile-editor__fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .profile-editor__fields {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  }

  .profile-editor__actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
